// Улики карточки: измерение РЯДОМ со своей полосой.
//
// Замечание владельца 05.09.2026: карточка обязана показывать значения, из-за
// которых вердикт именно такой. Голое «сила 2,74» не говорит ничего — смысл ему
// даёт полоса, в которую число должно попасть.
//
// Полоса у следа ДВУСТОРОННЯЯ, и это главное здесь. Ниже 1,35 — рябь, выше
// 3,0 — уже не пена, а соседний предмет в кадре (облако отражает треть
// падающего света). Показав «порог 1,35», мы научили бы читать карточку
// наоборот: «чем больше, тем вернее».
//
// Пороги приезжают ОТВЕТОМ ручки (`thresholds` в `src/cabinet/imagery_view.py`).
// Вторая копия здесь разъехалась бы с прибором молча, а карточка продолжала бы
// выглядеть убедительно.

export interface Thresholds {
  wake_strength: [number, number]
  wake_width_m: [number, number]
  wake_rings: number
  area_px: number
  background_ratio: number
  /** Порог силы РАДАРНОЙ линии, сигмы. Односторонний: мера безразмерна, и
   *  «слишком сильной» линии не бывает — в отличие от оптики, где очень яркая
   *  полоса означает облако. */
  wake_sigma_min: number
  /** Какая доля луча следа обязана лежать на воде по маске сцены (0…1). */
  wake_water_share: number
  /** Корпус (оптика, с 28.10.2026): наименьшая вытянутость ядра отметки,
   *  наибольшее число обрывков вокруг и соседних отметок в радиусе километра. */
  hull_aspect: number
  hull_clutter: number
  hull_neighbours: number
}

export type EvidenceKey =
  | 'wakeStrength' | 'wakeWidth' | 'wakeRings' | 'shadow'
  | 'wakeSigma' | 'wakeOffset' | 'wakeWater' | 'hullShape' | 'hullClean'

export interface EvidenceRow {
  key: EvidenceKey
  /** Числа для подстановки в перевод: слова подставляет компонент. */
  values: Record<string, number>
  /** Говорит ли эта улика ЗА судно. */
  ok: boolean
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** Строки улик по измерениям отметки. Пустой список — порогов не прислали.
 *
 *  Отсутствующее измерение строки НЕ ДАЁТ: отметки, записанные до появления
 *  замера формы, её не несут вовсе, и ноль на этом месте читался бы как
 *  измеренная нулевая ширина. «Не мерили» и «ноль» — разные ответы. */
export function evidenceRows(
  evidence: Record<string, unknown> | null | undefined,
  thresholds: Thresholds | null | undefined,
): EvidenceRow[] {
  if (!evidence || !thresholds) return []
  const rows: EvidenceRow[] = []

  // РАДАР. Мера другая — отклонение в единицах собственного разброса, а не
  // отношение яркостей, — поэтому и ключи свои. Оптические строки при этом не
  // строятся вовсе: их измерений у радарной отметки нет.
  const sigma = asNumber(evidence.wake_strength_sigma)
  if (sigma != null) {
    rows.push({ key: 'wakeSigma', values: { value: sigma, lo: thresholds.wake_sigma_min },
                ok: sigma >= thresholds.wake_sigma_min })
    const offset = asNumber(evidence.wake_offset_m)
    if (offset != null) {
      // Смещение — не улика, а МЕСТО: снос движущейся цели вдоль азимутальной
      // оси съёмки. Вердикта у строки нет, потому что судить тут не о чем.
      rows.push({ key: 'wakeOffset', values: { value: Math.abs(offset) }, ok: true })
    }
    return rows
  }

  // КОРПУС первым: вердикт «судно» на оптике с 28.10.2026 выносит он, а след
  // даёт курс. Отметки старше корпуса не несут — строк у них нет.
  const aspect = asNumber(evidence.hull_aspect)
  if (aspect != null) {
    rows.push({ key: 'hullShape', values: { value: aspect, lo: thresholds.hull_aspect },
                ok: aspect >= thresholds.hull_aspect })
    const clutter = asNumber(evidence.hull_clutter) ?? 0
    const neighbours = asNumber(evidence.neighbours_1km) ?? 0
    rows.push({ key: 'hullClean', values: { clutter, neighbours },
                ok: clutter <= thresholds.hull_clutter && neighbours <= thresholds.hull_neighbours })
  }

  const strength = asNumber(evidence.wake_strength)
  if (strength != null) {
    const [lo, hi] = thresholds.wake_strength
    rows.push({ key: 'wakeStrength', values: { value: strength, lo, hi },
                ok: strength >= lo && strength <= hi })
  }

  const width = asNumber(evidence.wake_width_m)
  if (width != null) {
    const [, hi] = thresholds.wake_width_m
    rows.push({ key: 'wakeWidth', values: { value: width, hi }, ok: width <= hi })
  }

  const ringCount = asNumber(evidence.wake_rings)
  const held = asNumber(evidence.wake_rings_ok)
  if (ringCount != null && held != null) {
    rows.push({ key: 'wakeRings', values: { value: held, total: ringCount },
                ok: ringCount >= thresholds.wake_rings && held === ringCount })
  }

  // Лежит ли след на ВОДЕ. Урез берега и кромка облака проходят и силу, и
  // форму — отличает их только эта доля (разбор 11.09.2026). В процентах:
  // «0,43» человек читает хуже, чем «43 %».
  const water = asNumber(evidence.wake_water_share)
  if (water != null) {
    const lo = thresholds.wake_water_share
    rows.push({ key: 'wakeWater', values: { value: Math.round(water * 100), lo: Math.round(lo * 100) },
                ok: water >= lo })
  }

  if (typeof evidence.has_shadow === 'boolean') {
    // Тень против солнца говорит «облако»: её ОТСУТСТВИЕ — довод за судно.
    rows.push({ key: 'shadow', values: {}, ok: evidence.has_shadow === false })
  }
  return rows
}
